import { Outlet, createFileRoute, useRouterState } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { AppShell, ImpersonationBanner, useAtLeast } from '@wundo/ui';
import { fetchCurrentUser, type CurrentUser } from '@wundo/client/auth.ts';
import {
  isGuestVisit,
  stopImpersonation,
  useImpersonatedBy,
} from '@wundo/client/impersonation.ts';
import { requireSession } from '@wundo/client/session.ts';
import { ActiveOrganisationProvider } from '@/components/ActiveOrganisationProvider.tsx';
import { GlobalOrganisationPicker } from '@/components/GlobalOrganisationPicker.tsx';
import { OrganisationFilterSlot } from '@/components/organisationFilterSlot.ts';
import { GroupNav } from '@/components/GroupNav.tsx';
import { activeGroup, groupItems, visibleGroups } from '@/components/navConfig.ts';
import { AdminMobileHeader, AdminRail, AdminTabBar } from '@/components/navigation.tsx';
import { BackendUnreachable } from '@/components/BackendUnreachable.tsx';
import { loginUrl } from '@/lib/config.ts';
import { personName } from '@/lib/personName.ts';
import { reportError } from '@/lib/reportError.ts';
import { useReloadOnIdentityChange } from '@wundo/client/useReloadOnIdentityChange.ts';
import { isInternalOrganisation, requireStaff } from '@/lib/staffGate.ts';
import { useVersionCheck } from '@/lib/versionCheck.ts';

/*
 * The console proper. Every page inside it renders in the shell and behind both
 * guards, so neither has to be repeated per route.
 */
export const Route = createFileRoute('/_app')({
  beforeLoad: async ({ context, location }) => {
    /*
     * The login screen lives on the customer app, so this is a navigation off
     * this origin rather than a route change. It never returns without a user
     * in the cache, which is what the staff gate then reads.
     */
    await requireSession(context.queryClient, location.href, { loginUrl: loginUrl() });
    await requireStaff(context.queryClient);
  },
  component: AppLayout,
  /* Reached when a guard rethrows: the check failed, rather than answering "no". */
  errorComponent: BackendUnreachable,
});

/* Below the breakpoint the mobile topbar carries the picker instead. */
const ORGANISATION_FILTER = (
  <GlobalOrganisationPicker className='max-w-80 max-mobile:hidden' />
);

export function AppLayout() {
  /*
   * Primed by the session guard, so this resolves from the cache on the first
   * render rather than costing a second round trip.
   */
  const { data: user } = useQuery({
    queryKey: ['currentUser'],
    queryFn: fetchCurrentUser,
  });

  /*
   * Staff signed in as somebody. Both halves of who that makes this tab are
   * watched together: stopping "Login as" changes the user, starting it changes
   * only the impersonator.
   */
  const impersonatedBy = useImpersonatedBy();
  useReloadOnIdentityChange(
    user && impersonatedBy !== undefined
      ? `${user.id}:${impersonatedBy ?? ''}`
      : undefined,
  );

  useVersionCheck();

  const internal = isInternalOrganisation(user);

  /*
   * The pages of the group you are standing in, as a sidebar beside the rail —
   * from the desk breakpoint up, which is the width that has the room for them.
   * Below it the rail tile's flyout holds the same list, and the two are the
   * same question asked once so only ever one of them is in the page. A group
   * holding one page this session may open has nothing to list either way.
   */
  const desk = useAtLeast('desk');
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const group = activeGroup(visibleGroups(internal), pathname);
  const secondary = desk && group && groupItems(group).length > 1 ? group : undefined;

  /* The mobile topbar carries the picker too, so the provider goes around the shell rather than the Outlet. */ return (
    <ActiveOrganisationProvider>
      <AppShell
        /* Tables and dashboards, at every width — the sidebar is a track, not a slice of the page. */
        contentWidth='wide'
        rail={<AdminRail user={user} internal={internal} />}
        sidebar={
          secondary ? <GroupNav group={secondary} pathname={pathname} /> : undefined
        }
        mobileNav={<AdminMobileHeader user={user} />}
        tabBar={<AdminTabBar internal={internal} />}
        banner={
          impersonatedBy && user ? (
            <ImpersonationBanner
              kind={isGuestVisit(user, impersonatedBy) ? 'visit' : 'loginAs'}
              stopLabel='Stop'
              /*
               * A full reload rather than a re-render: every screen and every
               * cached request behind this banner was assembled for the person
               * being impersonated, not for the staff member getting their own
               * session back.
               */
              onStop={() =>
                void stopImpersonation()
                  .catch((error: unknown) =>
                    reportError(error, { action: 'stopImpersonation' }),
                  )
                  .finally(() => window.location.reload())
              }
            >
              {bannerLine(user, impersonatedBy)}
            </ImpersonationBanner>
          ) : undefined
        }
      >
        <OrganisationFilterSlot.Provider value={ORGANISATION_FILTER}>
          <Outlet />
        </OrganisationFilterSlot.Provider>
      </AppShell>
    </ActiveOrganisationProvider>
  );
}

/** A guest visit also names the organisation and role: the guest account carries the superadmin's own name. */
function bannerLine(user: CurrentUser, impersonatedBy: string): string {
  const name = personName(user);
  if (!isGuestVisit(user, impersonatedBy)) {
    return `You are signed in as ${name}`;
  }

  const role = user.role === 'admin' ? 'an admin' : 'a member';
  return `You are working as ${name} in ${user.organisation?.name ?? 'this organisation'}, as ${role}.`;
}
